import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import Table from "../../../../../components/table/table";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import { getResultsDeliveredRequest } from "../../../../../redux/orders/slice";
import { downloadInspectionResultsRequest } from "../../../../../utils/APICalls";
import { getInspectionServicesRequest } from "../../../../../redux/inspection-firms/slice";

interface ClientResultsProps {
  clientDetails?: any;
}

export default function ClientResults({ clientDetails }: ClientResultsProps) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any[]>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [filterParams, setFilterParams] = useState<any>({
    ClientId: clientDetails?.id,
    OrderNumber: "",
    InspectionType: "",
    CompletedAt: "",
    LoanNumber: "",
    PropertyName: "",
    Address: "",
    City: "",
    State: "",
    Zip: "",
    Page: 1,
    PageSize: 10,
    sortColumn: "",
    sortOrder: "",
  });

  const { loading, resultsDelivered } = useSelector(
    (state: any) => state.orders
  );
  const { inspectionServices } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const [inspectionTypeOptions, setInspectionTypeOptions] = useState<string[]>(
    []
  );

  const rowCountRef = useRef(resultsDelivered?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (resultsDelivered?.totalCount !== undefined) {
      rowCountRef.current = resultsDelivered?.totalCount;
    }
    return rowCountRef.current;
  }, [resultsDelivered?.totalCount]);

  const columns: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: "Order ID",
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.orderNumber ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="OrderNumber"
          field="OrderNumber"
          sortField="OrderNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "inspectionType",
      headerName: "Inspection Type",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.inspectionType ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          key="InspectionType"
          field="InspectionType"
          sortField="InspectionType"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
          values={inspectionTypeOptions}
        />
      ),
    },
    {
      field: "completedAt",
      headerName: "Results Delivered Date",
      width: 230,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          key="CompletedAt"
          field="CompletedAt"
          sortField="CompletedAt"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "loanNumber",
      headerName: "Loan #",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.loanNumber ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="LoanNumber"
          field="LoanNumber"
          sortField="LoanNumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "propertyName",
      headerName: "Property Name",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.propertyName ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="PropertyName"
          field="PropertyName"
          sortField="PropertyName"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.street ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="Address"
          field="Address"
          sortField="Address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.city ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="City"
          field="City"
          sortField="City"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.state ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="State"
          field="State"
          sortField="State"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.propertyRecord?.zip ?? "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key="Zip"
          field="Zip"
          sortField="Zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterResults}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams((prev: any) => ({
      ...prev,
      ClientId: clientDetails?.id,
      Page: 1,
      PageSize: 10,
    }));
  }, [clientDetails]); // eslint-disable-line

  useEffect(() => {
    if (!inspectionServices || inspectionServices.length === 0) {
      dispatch(getInspectionServicesRequest());
      return;
    }

    const types = inspectionServices.reduce((acc: string[], item: any) => {
      if (item.inspectionType && !acc.includes(item.inspectionType)) {
        acc.push(item.inspectionType);
      }
      return acc;
    }, []);

    setInspectionTypeOptions(types);
  }, [inspectionServices, dispatch]);

  useEffect(() => {
    const items = (resultsDelivered?.items ||
      resultsDelivered?.data ||
      []) as any[];
    const dataWithIds = items.map((item: any, index: number) => ({
      ...item,
      id: item.id || item.orderId || item.orderNumber || index,
    }));
    setRowData(dataWithIds);
  }, [resultsDelivered]);

  useEffect(() => {
    const filters: any = { ...filterParams };

    Object.keys(filters).forEach((key) => {
      if (
        filters[key] === "" ||
        filters[key] === null ||
        filters[key] === undefined
      ) {
        delete filters[key];
      }
    });

    // Map UI field CompletedAt to API param CompletedOn
    if (filters.CompletedAt) {
      filters.CompletedOn = filters.CompletedAt;
      delete filters.CompletedAt;
    }

    if (!filters.ClientId && clientDetails?.id) {
      filters.ClientId = clientDetails.id;
    }

    // Map UI CompletedAt to API CompletedOn
    if (filters.CompletedAt) {
      filters.CompletedOn = filters.CompletedAt;
      delete filters.CompletedAt;
    }

    // Map internal sortColumn/sortOrder to API SortColumn/SortOrder
    if (filters.sortColumn && filters.sortOrder) {
      filters.SortColumn = filters.sortColumn;
      filters.SortOrder = filters.sortOrder;
    }
    delete filters.sortColumn;
    delete filters.sortOrder;

    if (!filters.SortColumn || !filters.SortOrder) {
      delete filters.SortColumn;
      delete filters.SortOrder;
    }

    if (filters.Page === undefined || filters.Page === null) {
      filters.Page = 1;
    }
    if (filters.PageSize === undefined || filters.PageSize === null) {
      filters.PageSize = 10;
    }

    if (filters.ClientId) {
      dispatch(getResultsDeliveredRequest(filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParams.ClientId,
    filterParams.OrderNumber,
    filterParams.InspectionType,
    filterParams.CompletedAt,
    filterParams.LoanNumber,
    filterParams.PropertyName,
    filterParams.Address,
    filterParams.City,
    filterParams.State,
    filterParams.Zip,
    filterParams.Page,
    filterParams.PageSize,
    filterParams.sortColumn,
    filterParams.sortOrder,
  ]);

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterResults = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...rest } = filterParams as any;
          setFilterParams(rest);
        } else if (
          data[key] !== undefined &&
          data[key] !== null &&
          data[key] !== ""
        ) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      ClientId: clientDetails?.id,
      OrderNumber: "",
      InspectionType: "",
      CompletedAt: "",
      LoanNumber: "",
      PropertyName: "",
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Page: 1,
      PageSize: 10,
      sortColumn: "",
      sortOrder: "",
    });
    setFilterResetKey((prev) => prev + 1);
  };

  const onDownloadResultsClick = async () => {
    if (!rowSelectionModel.length) return;

    const selectedRows = rowData.filter((row) =>
      rowSelectionModel.includes(row.id)
    );
    const orderIds = selectedRows
      .map((row) => row.orderId || row.id)
      .filter(Boolean);

    if (!orderIds.length) return;

    try {
      setIsDownloading(true);
      const response = await downloadInspectionResultsRequest(
        orderIds as string[]
      );
      if (response && response.status === 200) {
        const mimeType = response.headers["content-type"] || "application/zip";
        const blob = new Blob([response.data], {
          type: mimeType,
        });

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "Results";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch {
      // optional: could hook into global error notification
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          sx={{ mr: 2, textTransform: "uppercase" }}
          onClick={onDownloadResultsClick}
          disabled={!rowSelectionModel.length}
        >
          DOWNLOAD RESULTS
        </Button>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading || isDownloading}
          columnDef={columns}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          checkboxSelection={true}
          onPaginationModelChange={(newPaginationModel: any) => {
            setPaginationModel(newPaginationModel);
            setFilterParams((prev: any) => ({
              ...prev,
              Page: newPaginationModel.page + 1,
              PageSize: newPaginationModel.pageSize,
            }));
          }}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>
    </>
  );
}
